import React from 'react';
import { CalendarIcon, UtensilsIcon } from 'lucide-react';
export interface CellarDoorSectionProps {
  /** Optional data-id for component identification */
  'data-id'?: string;
  /** Image URL for the left side */
  imageSrc: string;
  /** Alt text for the image */
  imageAlt: string;
  /** Small label text above the title */
  label?: string;
  /** Main heading text */
  title: string;
  /** Primary description paragraph */
  description: string;
  /** Optional secondary description or note */
  note?: string;
  /** Button text */
  buttonText?: string;
  /** Button click handler */
  onButtonClick?: () => void;
  /** Second button text */
  secondButtonText?: string;
  /** Second button click handler */
  onSecondButtonClick?: () => void;
  /** Accent colour for decorative elements */
  accentColor?: string;
  /** Reverse layout (content on left, image on right) */
  reverse?: boolean;
}
export const CellarDoorSection: React.FC<CellarDoorSectionProps> = ({
  'data-id': dataId,
  imageSrc,
  imageAlt,
  label = 'Experience',
  title,
  description,
  note,
  buttonText = 'Learn More',
  onButtonClick,
  secondButtonText,
  onSecondButtonClick,
  accentColor = 'pink',
  reverse = false
}) => {
  const accentColorClasses = {
    pink: {
      line: 'bg-brand-pink',
      text: 'text-brand-pink',
      border: 'border-brand-pink'
    },
    blue: {
      line: 'bg-blue-600',
      text: 'text-blue-600',
      border: 'border-blue-200'
    },
    green: {
      line: 'bg-green-600',
      text: 'text-green-600',
      border: 'border-green-200'
    },
    purple: {
      line: 'bg-purple-600',
      text: 'text-purple-600',
      border: 'border-purple-200'
    }
  };
  const colors = accentColorClasses[accentColor as keyof typeof accentColorClasses] || accentColorClasses.pink;
  const imageSection = <div className={`relative aspect-[4/3] overflow-hidden rounded border-8 border-gray-100 shadow-xl bg-gray-300 order-2 ${reverse ? 'lg:order-2' : 'lg:order-1'}`} data-id="element-226">
      <img src={imageSrc} alt={imageAlt} className="w-full h-full object-cover" data-id="element-227" />
    </div>;
  const contentSection = <div className={`space-y-6 order-1 ${reverse ? 'lg:order-1 lg:pr-4' : 'lg:order-2 lg:pl-4'}`} data-id="element-228">
      <div className="space-y-3" data-id="element-229">
        <div className={`w-12 h-0.5 ${colors.line}`} data-id="element-230"></div>
        <div className={`${colors.text} text-xs tracking-[0.3em] font-light uppercase`} data-id="element-231">
          {label}
        </div>
        <h2 className="text-5xl font-serif text-gray-900 leading-tight" data-id="element-232">
          {title}
        </h2>
      </div>
      <div className="space-y-4" data-id="element-233">
        <p className="text-gray-600 text-lg leading-relaxed" data-id="element-234">{description}</p>
        {note && <p className={`text-gray-500 text-sm leading-relaxed pl-4 border-l-2 ${colors.border}`} data-id="element-235">
            {note}
          </p>}
      </div>
      <div className="pt-4 flex flex-col sm:flex-row gap-4" data-id="element-236">
        <button onClick={onButtonClick} className="bg-brand-grey text-white px-8 py-4 hover:bg-neutral-700 transition-colors font-medium inline-flex items-center justify-center gap-2 rounded-none" data-id="element-237">
          <CalendarIcon className="w-5 h-5" data-id="element-238" />
          {buttonText}
        </button>
        {secondButtonText && <button onClick={onSecondButtonClick} className="border-2 border-brand-grey text-brand-grey px-8 py-3 hover:bg-brand-grey hover:text-white transition-colors font-medium inline-flex items-center justify-center gap-2 rounded-none" data-id="element-239">
            <UtensilsIcon className="w-5 h-5" data-id="element-240" />
            {secondButtonText}
          </button>}
      </div>
    </div>;
  return <section className="py-10 px-6 bg-white w-full" data-id="element-241">
      <div className="max-w-7xl mx-auto" data-id="element-242">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-243">
          {contentSection}
          {imageSection}
        </div>
      </div>
    </section>;
};